import type { Dispatch, FC, FormEventHandler, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../lib/format';

export interface BenefitTypeFormState {
  name: string;
  amount: string | number;
  is_taxable: boolean;
  formula: string;
}

export interface EmployeeBenefitFormState {
  employee_id: string;
  benefit_type_id: string;
  amount: string | number;
  start_date: string;
  end_date: string;
}

export interface BenefitType {
  id: number | string;
  name: string;
  amount: number;
  is_taxable: boolean;
}

export interface EmployeeBenefit {
  id: number | string;
  benefit_name: string;
  amount: number;
  start_date?: string | null;
  end_date?: string | null;
}

export interface BenefitsEmployee {
  id: number | string;
  full_name: string;
  employee_code: string;
}

export interface BenefitTypesCardProps {
  form: BenefitTypeFormState;
  setForm: Dispatch<SetStateAction<BenefitTypeFormState>>;
  benefitTypes: BenefitType[];
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  activeLanguage: string;
}

export const BenefitTypesCard: FC<BenefitTypesCardProps> = ({
  form,
  setForm,
  benefitTypes,
  isSubmitting,
  onSubmit,
  activeLanguage,
}) => {
  const { t } = useTranslation();

  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>{t('benefitTypes')}</h2>
      <div className="split split-three">
        <label>
          {t('benefitName')}
          <input
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          />
        </label>
        <label>
          {t('benefitAmount')}
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
          />
        </label>
        <label>
          {t('taxableBenefit')}
          <select
            value={String(form.is_taxable)}
            onChange={(event) => setForm((current) => ({ ...current, is_taxable: event.target.value === 'true' }))}
          >
            <option value="true">{t('yes')}</option>
            <option value="false">{t('no')}</option>
          </select>
        </label>
      </div>
      <label>
        {t('benefitFormula')}
        <input
          value={form.formula}
          onChange={(event) => setForm((current) => ({ ...current, formula: event.target.value }))}
        />
      </label>
      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? t('saving') : t('createBenefitType')}
      </button>
      {benefitTypes.length ? (
        <ul className="list compact-list">
          {benefitTypes.map((benefitType) => (
            <li key={benefitType.id}>
              <div>
                <strong>{benefitType.name}</strong>
                <span>
                  {formatCurrency(benefitType.amount, activeLanguage)} •{' '}
                  {benefitType.is_taxable ? t('taxableBenefit') : t('nonTaxableAllowance')}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>{t('noBenefits')}</p>
      )}
    </form>
  );
};

export interface EmployeeBenefitsCardProps {
  form: EmployeeBenefitFormState;
  setForm: Dispatch<SetStateAction<EmployeeBenefitFormState>>;
  employees: BenefitsEmployee[];
  benefitTypes: BenefitType[];
  employeeBenefits: EmployeeBenefit[];
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  activeLanguage: string;
}

export const EmployeeBenefitsCard: FC<EmployeeBenefitsCardProps> = ({
  form,
  setForm,
  employees,
  benefitTypes,
  employeeBenefits,
  isSubmitting,
  onSubmit,
  activeLanguage,
}) => {
  const { t } = useTranslation();

  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>{t('assignBenefit')}</h2>
      <label>
        {t('employee')}
        <select
          value={form.employee_id}
          onChange={(event) => setForm((current) => ({ ...current, employee_id: event.target.value }))}
        >
          <option value="">{t('selectEmployee')}</option>
          {employees.map((employee) => (
            <option key={employee.id} value={employee.id}>
              {employee.full_name} ({employee.employee_code})
            </option>
          ))}
        </select>
      </label>
      <div className="split split-three">
        <label>
          {t('benefitTypes')}
          <select
            value={form.benefit_type_id}
            onChange={(event) => setForm((current) => ({ ...current, benefit_type_id: event.target.value }))}
          >
            <option value="">{t('notAvailable')}</option>
            {benefitTypes.map((benefitType) => (
              <option key={benefitType.id} value={benefitType.id}>
                {benefitType.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('benefitAmount')}
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
          />
        </label>
        <label>
          {t('effectiveStart')}
          <input
            value={form.start_date}
            onChange={(event) => setForm((current) => ({ ...current, start_date: event.target.value }))}
          />
        </label>
      </div>
      <label>
        {t('effectiveEnd')}
        <input
          value={form.end_date}
          onChange={(event) => setForm((current) => ({ ...current, end_date: event.target.value }))}
        />
      </label>
      <button
        type="submit"
        disabled={isSubmitting || !form.employee_id || !form.benefit_type_id}
      >
        {isSubmitting ? t('saving') : t('assignBenefit')}
      </button>
      {employeeBenefits.length ? (
        <ul className="list compact-list">
          {employeeBenefits.map((benefit) => (
            <li key={benefit.id}>
              <div>
                <strong>{benefit.benefit_name}</strong>
                <span>
                  {formatCurrency(benefit.amount, activeLanguage)} • {benefit.start_date || t('notAvailable')}{' '}
                  {benefit.end_date ? `to ${benefit.end_date}` : ''}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>{t('noBenefits')}</p>
      )}
    </form>
  );
};
